/**
 * useChartMindIAccess — gate for ChartMind-I product surfaces.
 *
 * True when:
 * - JWT global admin, OR
 * - chartMindIAccess/{uid} exists
 *
 * Mirrors useClinicalHudAccess (admin bypass = isGlobalAdmin only).
 */
import { useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { subscribeToChartMindIAccess } from "../services/chartMindIAccess";

export function useChartMindIAccess() {
  const { user, isGlobalAdmin, userLoading } = useAuth();
  const [hasDocAccess, setHasDocAccess] = useState(false);
  const [docLoading, setDocLoading] = useState(true);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;
  const privileged = Boolean(isGlobalAdmin);

  useEffect(() => {
    if (userLoading) {
      setDocLoading(true);
      return undefined;
    }

    if (!isRealAccount || privileged) {
      setHasDocAccess(false);
      setDocLoading(false);
      return undefined;
    }

    setDocLoading(true);
    const unsub = subscribeToChartMindIAccess(uid, (data) => {
      setHasDocAccess(Boolean(data));
      setDocLoading(false);
    });
    return () => unsub();
  }, [uid, isRealAccount, userLoading, privileged]);

  const canAccess = Boolean(isRealAccount && (privileged || hasDocAccess));
  const loading = Boolean(
    userLoading || (isRealAccount && !privileged && docLoading),
  );

  return { canAccess: loading ? false : canAccess, loading };
}

export default useChartMindIAccess;
